import { useInfiniteQuery } from '@tanstack/react-query';
import { projectsApi, type ProjectActivityEvent } from '@/lib/apiClient';
import { fmtDateTime } from '@/lib/dates';

/**
 * The project Overview "Activity" card — who did what in this job, across every
 * domain, newest first.
 *
 * Why not reuse <ActivityTimeline>: that component renders ONE entity's story
 * oldest-first with no notion of which record an event belongs to. A job's feed
 * is the other way round — newest first, spanning every domain — so each row
 * needs a record chip ("RFI-004") and a jump to the right tab, and the rows need
 * day grouping to stay scannable. The visual language (dot + connector) is kept
 * deliberately identical so the two read as the same idea.
 */

/**
 * action → human label. Server slugs are the source of truth (see
 * docs/activity-log-contract.md); an unmapped action falls back to its raw slug
 * so a newly-logged event still renders sensibly rather than disappearing.
 */
const ACTIVITY_LABELS: Record<string, string> = {
  // Project itself
  'project-edited': 'Edited the job',
  'checklist-updated': 'Updated the mobilization checklist',
  'note-added': 'Added a note',
  'note-deleted': 'Deleted a note',
  'project-deleted': 'Deleted the job',
  'project-auto-archived': 'Archived the job automatically',
  'phases-updated': 'Updated the phase schedule',
  'projected-hours-override': 'Changed projected hours',
  'field-leader-assigned': 'Assigned a field leader',
  'field-leader-unassigned': 'Removed a field leader',
  'field-leader-starred': 'Set the primary field leader',
  'bid-linked': 'Linked the job to its bid',
  'bid-link-moved': 'Bid moved to the real job',
  // RFIs + submittals (shared slugs — the record chip says which)
  'created': 'Created',
  'sent-to-assignee': 'Sent to assignee',
  'sent-to-reviewer': 'Sent to reviewer',
  'revision-sent': 'Revision sent',
  'sent-time-corrected': 'Sent time corrected',
  'marked-not-sent': 'Marked as not sent',
  'responded': 'Response logged',
  'decision': 'Decision logged',
  'reopened': 'Reopened',
  'ball-reassigned': 'Ball reassigned',
  'details-edited': 'Details edited',
  // Attachments
  'file-uploaded': 'Uploaded a file',
  'file-removed': 'Removed a file',
  // Daily logs
  'daily-log-started': 'Started a daily log',
  'daily-log-updated': 'Updated a daily log',
  'daily-log-submitted': 'Submitted a daily log',
  // Site photos
  'site-photos-posted': 'Posted site photos',
  'site-photos-removed': 'Removed a site photo post',
  // Schedule
  'schedule-imported': 'Imported the MS Project schedule',
  // Mobilization
  'mobilization-added': 'Added a mobilization',
  'mobilization-updated': 'Updated a mobilization',
  'mobilization-removed': 'Removed a mobilization',
  // Equipment
  'equipment-requested': 'Requested equipment',
  'equipment-updated': 'Updated an equipment request',
  'equipment-called-off': 'Called off equipment',
  'equipment-extended': 'Extended equipment',
  'equipment-removed': 'Removed an equipment request',
  // Receiving
  'order-created': 'Created a receiving order',
  'order-updated': 'Updated a receiving order',
  'order-removed': 'Removed a receiving order',
  // Shipping
  'shipping-requested': 'Requested a pickup or delivery',
  'shipping-updated': 'Updated a pickup or delivery request',
  'shipping-removed': 'Removed a pickup or delivery request',
  // Materials
  'material-added': 'Added a material',
  'material-updated': 'Updated a material',
  'material-removed': 'Removed a material',
  // Survey
  'survey-requested': 'Requested a survey',
  'survey-request-updated': 'Updated a survey request',
  'survey-log-added': 'Added a survey log',
  'survey-log-updated': 'Updated a survey log',
  'survey-log-deleted': 'Deleted a survey log',
  // QA / QC
  // Concrete AND rock/aggregate orders log these; the detail names a rock one.
  'pour-card-created': 'Added an order',
  'pour-card-updated': 'Updated a pour card',
  'pour-order-confirmed': 'Confirmed an order',
  'pour-marked-poured': 'Marked an order complete',
  'pour-order-cancelled': 'Cancelled an order',
  'pour-covered-by-main': 'Marked a pour covered by the main order',
  'pour-project-assigned': 'Assigned a pour card to this job',
  'pour-material-linked': 'Linked a pour to a material',
  'mix-design-added': 'Added a mix design',
  'mix-design-updated': 'Updated a mix design',
  'mix-design-retired': 'Retired a mix design',
  'mix-design-reinstated': 'Reinstated a mix design',
  'concrete-setup-added': 'Recorded the concrete supplier & BPO',
  'concrete-setup-updated': 'Updated the concrete supplier & BPO',
  // Change management
  'change-created': 'Created a change item',
  'change-status-set': 'Changed a change-item status',
  'change-sent-to-client': 'Sent a change to the client',
  'change-accepted': 'Recorded the client accepting a change',
  'change-acceptance-corrected': 'Corrected when the client accepted a change',
  'change-intacct-entered': 'Marked a change entered in Intacct',
  'change-amount-refreshed': 'Took the new Intacct amount for a change',
  // Documents + drawings
  'document-version-registered': 'Registered a document version',
  'drawing-set-registered': 'Registered a drawing set',
  'drawing-set-updated': 'Updated a drawing set',
  'drawing-set-removed': 'Removed a drawing set',
  // Manpower. NOTE the snake_case: this slug predates the kebab-case
  // convention and getRecentScheduleChanges queries for it literally.
  'crew_schedule_published': 'Published the crew schedule',
  'crew-future-cleared': 'Cleared the upcoming crew schedule',
  'crew-cell-edited': 'Edited a crew day',
  // Manpower requests (#303). The request itself lives in the /manpower inbox;
  // these pointers are so the job's own feed shows the ask and the answer.
  'manpower-request-raised': 'Requested a crew change',
  'manpower-request-reviewed': 'Marked a crew request reviewed',
  'manpower-request-booked': 'Booked a crew request',
  'manpower-request-declined': 'Declined a crew request',
  'manpower-request-reopened': 'Reopened a crew request',
  'manpower-request-co-executed': 'A PCO crew request became a CO request',
  'maintenance-flag-set': 'Changed the maintenance flag',
  'gc-loan-flag-set': 'Changed the GC-loan flag',
  // Punch list (#518). The detail line carries the item number and where the
  // ball went, so the labels only say what happened.
  'punch-list-started': 'Started the punch list',
  'punch-settings-saved': 'Changed the punch list settings',
  'punch-list-published': 'Published the punch list',
  'punch-list-closed-out': 'Closed out the punch list',
  'punch-list-reopened': 'Reopened the punch list',
  'punch-sheet-sent': 'Sent a punch sheet',
  'punch-item-added': 'Added a punch item',
  'punch-item-edited': 'Edited a punch item',
  'punch-item-sent': 'Sent a punch item',
  'punch-item-fixed': 'Marked a punch item fixed',
  'punch-item-accepted': 'Accepted a punch item fix',
  'punch-item-rejected': 'Sent a punch item back',
  'punch-item-approved': 'Recorded architect/owner approval',
  'punch-item-reopened': 'Reopened a punch item',
  'punch-ball-passed': 'Passed a punch item',
  'punch-item-removed': 'Removed a punch item',
  'punch-items-bulk-changed': 'Changed several punch items',
  // Project directory (#598). The detail names the person, their company and their
  // role on the job. The address-book actions carry no project, so they never reach
  // a job's feed; they are labeled for completeness, as the owner contracts are.
  'project-contact-added': 'Added a contact to the job',
  'project-contact-updated': 'Changed a contact’s role on the job',
  'project-contact-removed': 'Took a contact off the job',
  'contact-added': 'Added a person to the address book',
  'contact-updated': 'Updated a person in the address book',
  'contact-company-added': 'Added a company to the address book',
  'contact-company-updated': 'Updated a company in the address book',
  // Meetings (#644). The detail names the meeting ("OAC Meeting #3"), and for a
  // save, what moved: its details, and how many items were added, updated or removed.
  'meeting-created': 'Added a meeting',
  'meeting-updated': 'Updated a meeting’s agenda or minutes',
  'meeting-removed': 'Removed a meeting',
  // Contract tracker (#501). The detail line names the vendor and what changed.
  // The owner-contract actions are labelled for completeness only: their events
  // carry no project, so they never reach a job's feed.
  'sub-contract-added': 'Started tracking a subcontract',
  'sub-contract-updated': 'Updated a subcontract',
  'sub-contract-cleared': 'Cleared a subcontractor to report to site',
  'sub-contract-voided': 'Voided a subcontract',
  'sub-contract-restored': 'Restored a voided subcontract',
  'sub-contract-removed': 'Removed a subcontract',
  'owner-contract-added': 'Started tracking an owner contract',
  'owner-contract-updated': 'Updated an owner contract',
  'owner-contract-removed': 'Removed an owner contract',
  // Financials (only visible with financials access)
  'forecast-saved': 'Saved a cost forecast',
  'budget-transferred': 'Transferred budget',
  'buyout-plan-saved': 'Saved a buyout plan',
  'accruals-saved': 'Saved month-end accruals',
};

/** Which workspace tab a record lives on, for the chip's jump link. */
const ENTITY_TABS: Record<string, string> = {
  'rfi': 'rfis',
  'submittal': 'submittals',
  'daily-log': 'daily',
  'mobilization': 'mobilization',
  'equipment': 'equipment',
  'receiving': 'receiving',
  'shipping': 'shipping',
  'material': 'materials',
  'survey': 'survey',
  'survey-log': 'survey',
  'pour-card': 'qaqc',
  'mix-design': 'qaqc',
  'concrete-setup': 'qaqc',
  'inspection': 'qaqc',
  'change': 'changes',
  'document': 'documents',
  'drawing-set': 'drawings',
  'manpower-request': 'manpower',
  'site-photo-post': 'photos',
  'punch-item': 'punch',
  'punch-list': 'punch',
  'sub-contract': 'contracts',
  'project-contact': 'contacts',
  'meeting': 'meetings',
};

/** Extra URL params for a chip whose record lives in a tab's sub-view. None
 *  today: site photo posts went to Daily Logs ▸ ?view=photos until they got
 *  their own Photos tab (#665). */
const ENTITY_TAB_PARAMS: Record<string, Record<string, string>> = {};

/**
 * The URL params a chip opens its tab with: the static ones above, plus the
 * record's own id for tabs that open one record from the URL (a punch item's
 * drawer is ?item=<id>).
 */
const ENTITY_ID_PARAM: Record<string, string> = { 'punch-item': 'item', 'meeting': 'meeting' };

/** Events whose record no longer exists: their chip opens the tab, not the record. */
const RECORD_GONE_ACTIONS = new Set(['punch-item-removed', 'meeting-removed']);

const recordKey = (e: ProjectActivityEvent) => `${e.entityType}:${e.entityId}`;

/**
 * The records a loaded event says were removed. Every event about one of them
 * opens the tab, not the record, or an earlier "created" would open a record
 * that 404s (Codex on #723). The feed is newest-first, so a removal is always
 * loaded before the earlier events about the same record.
 */
export function goneRecords(events: ProjectActivityEvent[]): Set<string> {
  return new Set(events.filter((e) => RECORD_GONE_ACTIONS.has(e.action)).map(recordKey));
}

export function chipParams(event: ProjectActivityEvent, gone: Set<string>): Record<string, string> | undefined {
  const idParam = ENTITY_ID_PARAM[event.entityType];
  const fixed = ENTITY_TAB_PARAMS[event.entityType];
  if (!idParam || gone.has(recordKey(event))) return fixed;
  return { ...fixed, [idParam]: String(event.entityId) };
}

/** Short chip text per entity type. 'project' gets none — it IS this page. */
const ENTITY_NAMES: Record<string, string> = {
  'rfi': 'RFI',
  'submittal': 'Submittal',
  'daily-log': 'Daily log',
  'mobilization': 'Mobilization',
  'equipment': 'Equipment',
  'receiving': 'Receiving',
  'shipping': 'Shipping',
  'material': 'Material',
  'survey': 'Survey',
  'survey-log': 'Survey log',
  'pour-card': 'Pour card',
  'mix-design': 'Mix design',
  'concrete-setup': 'Concrete ordering',
  'inspection': 'Inspection',
  'change': 'Change',
  'document': 'Document',
  'drawing-set': 'Drawings',
  'manpower-request': 'Crew request',
  'site-photo-post': 'Photos',
  'punch-item': 'Punch item',
  'punch-list': 'Punch list',
  'sub-contract': 'Subcontract',
  'owner-contract': 'Owner contract',
  'project-contact': 'Contact',
  'meeting': 'Meeting',
};

const PAGE_SIZE = 15;

const css = `
  .pa-list { list-style: none; margin: 0; padding: 0; }
  .pa-day {
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-400);
    padding: var(--ew-s-3) 0 var(--ew-s-2); margin: 0;
  }
  .pa-day:first-child { padding-top: 0; }
  .pa-item { position: relative; padding: 0 0 var(--ew-s-4) var(--ew-s-5); }
  .pa-item:last-child { padding-bottom: 0; }
  /* Connector runs down the dot column, as in <ActivityTimeline>. */
  .pa-item::before {
    content: ''; position: absolute; left: 4px; top: 5px; bottom: 0;
    width: 2px; background: var(--ew-n-100);
  }
  .pa-item:last-child::before { display: none; }
  .pa-dot {
    position: absolute; left: 0; top: 4px; width: 10px; height: 10px;
    border-radius: 50%; background: var(--ew-quality); border: 2px solid #fff;
    box-shadow: 0 0 0 1px var(--ew-n-200);
  }
  .pa-hd { display: flex; align-items: baseline; gap: var(--ew-s-2); flex-wrap: wrap; }
  .pa-action {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm)/1.3 var(--ew-font); color: var(--ew-dark);
  }
  .pa-chip {
    font: var(--ew-fw-medium) 11px/1 var(--ew-font); color: var(--ew-n-600);
    background: var(--ew-n-50); border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-sm); padding: 3px 6px; cursor: pointer;
  }
  .pa-chip:hover { border-color: var(--ew-green); color: var(--ew-green); }
  /* Long field-diff strings wrap rather than stretching the card. */
  .pa-detail {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.45 var(--ew-font);
    color: var(--ew-n-600); margin-top: 3px; overflow-wrap: anywhere;
  }
  .pa-meta {
    font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-400); margin-top: 3px;
  }
  .pa-more {
    display: block; width: 100%; margin-top: var(--ew-s-3);
    background: none; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-sm);
    padding: 8px; cursor: pointer;
    font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-700);
  }
  .pa-more:hover { border-color: var(--ew-green); color: var(--ew-green); }
  .pa-empty {
    font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-400);
  }
`;

/**
 * Time only — the rows are grouped under a day heading, so repeating the date on
 * every line just crowds it out. Falls back to the full date+time if the
 * timestamp won't parse, so a bad value degrades to something rather than blank.
 */
function fmtTimeOnly(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fmtDateTime(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** First occurrence of each event id, order preserved. */
function dedupeById(events: ProjectActivityEvent[]): ProjectActivityEvent[] {
  const seen = new Set<number>();
  return events.filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)));
}

/** "Today" / "Yesterday" / "Tuesday, July 28" for a day heading. */
function dayHeading(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(new Date()) - startOf(d)) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
    // Only name the year once it isn't the current one.
    year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  });
}

export default function ProjectActivityCard({
  projectId,
  onOpenTab,
}: {
  projectId: number;
  onOpenTab: (key: string, extra?: Record<string, string>) => void;
}) {
  // Offset paging via useInfiniteQuery rather than re-fetching an ever-larger
  // limit: the API clamps limit at 100, so a growing-limit approach would
  // silently stop returning new rows after ~7 clicks while still reporting
  // hasMore — a "Show more" button that does nothing.
  const q = useInfiniteQuery({
    queryKey: ['projects', 'activity', projectId],
    queryFn: ({ pageParam }) =>
      projectsApi.activity(projectId, { limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, all) =>
      last.hasMore ? all.length * PAGE_SIZE : undefined,
    enabled: Number.isFinite(projectId),
    // The feed is a record, not a live ticker — a stale window avoids refetching
    // every time the user comes back to Overview.
    staleTime: 30_000,
  });

  // De-duplicate by id across pages. Offset paging over a feed that is still
  // being written to can return the same row twice: if three events land
  // between loading page 1 and clicking "Show more", page 2 starts three rows
  // earlier than expected, and React would then see duplicate keys.
  const events = dedupeById(q.data?.pages.flatMap((p) => p.events) ?? []);
  const gone = goneRecords(events);

  return (
    <div className="pw-card">
      <style>{css}</style>
      <h2 className="pw-card-hd">Activity</h2>
      <div style={{ padding: 'var(--ew-s-4)' }}>
        {q.isLoading ? (
          <p className="pa-empty">Loading…</p>
        ) : q.isError ? (
          <p className="pa-empty">Couldn’t load activity.</p>
        ) : events.length === 0 ? (
          <p className="pa-empty">
            No activity recorded yet. Edits, RFIs, daily logs, and crew publishes will show up here.
          </p>
        ) : (
          <>
            <ul className="pa-list">
              {events.map((e, i) => {
                const heading = dayHeading(e.createdAt);
                // A heading only when the day changes — the list is already
                // sorted newest-first, so comparing with the previous row is enough.
                const showDay = i === 0 || heading !== dayHeading(events[i - 1].createdAt);
                return (
                  <li key={e.id}>
                    {showDay && <p className="pa-day">{heading}</p>}
                    <div className="pa-item">
                      <span className="pa-dot" />
                      <div className="pa-hd">
                        <span className="pa-action">
                          {ACTIVITY_LABELS[e.action] ?? e.action}
                        </span>
                        <EntityChip event={e} gone={gone} onOpenTab={onOpenTab} />
                      </div>
                      {e.detail ? <div className="pa-detail">{e.detail}</div> : null}
                      <div className="pa-meta">
                        {e.actor?.displayName ?? e.actor?.email ?? (e.action === 'project-auto-archived' ? 'Intacct sync' : 'Unknown')} · {fmtTimeOnly(e.createdAt)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            {q.hasNextPage && (
              <button
                type="button"
                className="pa-more"
                disabled={q.isFetchingNextPage}
                onClick={() => q.fetchNextPage()}
              >
                {q.isFetchingNextPage ? 'Loading…' : 'Show more'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The record an event happened to, as a chip that jumps to its tab.
 * Nothing is rendered for 'project' events — the record is this page.
 * The chip opens the TAB, not the individual record: the tabs don't all accept
 * a deep-link param, and a chip that silently lands somewhere unhelpful is
 * worse than one that lands on the right list.
 */
function EntityChip({
  event,
  gone,
  onOpenTab,
}: {
  event: ProjectActivityEvent;
  /** goneRecords() of the loaded feed. */
  gone: Set<string>;
  onOpenTab: (key: string, extra?: Record<string, string>) => void;
}) {
  if (event.entityType === 'project') return null;
  const tab = ENTITY_TABS[event.entityType];
  const name = ENTITY_NAMES[event.entityType] ?? event.entityType;
  // Don't restate the type when the record's own number already carries it:
  // an RFI numbered "RFI-002" would otherwise read "RFI RFI-002", and a
  // submittal "SUB-005" is unambiguous on its own. Numbers are free text the PM
  // types, so this is a prefix check, not an assumption about the format.
  const label = event.entityLabel;
  const text = !label
    ? name
    : label.toLowerCase().startsWith(name.toLowerCase()) || /^(rfi|sub)[-\s]?\d/i.test(label)
      ? label
      : `${name} ${label}`;
  if (!tab) return <span className="pa-chip" style={{ cursor: 'default' }}>{text}</span>;
  return (
    <button type="button" className="pa-chip" onClick={() => onOpenTab(tab, chipParams(event, gone))} title={`Open ${name}`}>
      {text} →
    </button>
  );
}
